/*
 * Purpose: Open saved flow — Map (edit) and Mission (play) actions
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import { Link } from 'react-router-dom';
import { Map, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { missionMapHref } from '@/lib/appRoutes';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { cn } from '@/lib/utils';

export interface FlowOpenButtonsProps {
  flowId: string;
  /** Called before navigation — typically setCurrentWorkflow */
  onBeforeNavigate?: () => void;
  className?: string;
  compact?: boolean;
}

export function FlowOpenButtons({
  flowId,
  onBeforeNavigate,
  className,
  compact = false,
}: FlowOpenButtonsProps) {
  const mapHref = missionMapHref(flowId);
  const missionHref = missionMapHref(flowId);

  const handleClick = () => onBeforeNavigate?.();

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn('flex flex-wrap gap-1.5 shrink-0', className)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              actionTier="ghost"
              className={cn('h-8', compact ? 'px-2' : 'text-xs')}
              asChild
            >
              <Link to={mapHref} onClick={handleClick}>
                <Map className="h-3.5 w-3.5 shrink-0" />
                {!compact && <span className="ml-1.5 hidden sm:inline">Map</span>}
                <span className="sr-only">Edit flow map</span>
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Edit flow map</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              actionTier="secondary"
              className={cn('h-8', compact ? 'px-2' : 'text-xs')}
              asChild
            >
              <Link to={missionHref} onClick={handleClick}>
                <Play className="h-3.5 w-3.5 shrink-0" />
                {!compact && (
                  <span className="ml-1.5 hidden sm:inline">{PRODUCT_TERMS.mission}</span>
                )}
                <span className="sr-only">Open {PRODUCT_TERMS.mission}</span>
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Run {PRODUCT_TERMS.mission}</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

export default FlowOpenButtons;
